import { makeAutoObservable, runInAction } from 'mobx';

import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError, standardDeletedSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';

import { DELETE_GROUP } from './gql/deleteGroup';
import { GET_CREW_MEMBER_GROUPS } from './gql/getCrewMemberGroups';

class GroupsStore {
	groups = [];
	groupsMapped = new Map();
	selectedGroup = null;

	isLoading = false;
	isError = false;
	isNoGroups = false;

	selectedForDelete = '';
	isInDelete = false;
	isDeleting = false;

	constructor() {
		makeAutoObservable(this);
	}

	fetchGroups = async () => {
		this.isLoading = true;
		try {
			const response = await client.query({
				query: GET_CREW_MEMBER_GROUPS,
				variables: {},
				fetchPolicy: 'no-cache',
			});
			const mappedGroupArr = response.data.crewMemberGroups.map((group) => [
				group.id,
				{ ...group, loading: false },
			]);
			const mappedGroup = new Map(mappedGroupArr);
			runInAction(() => {
				this.groupsMapped = mappedGroup;
				this.groups = response.data.crewMemberGroups;
				this.isLoading = false;
				this.isNoGroups = mappedGroupArr.length === 0;
			});
		} catch (e) {
			notificationStore.setNotification(standardError);
		}
	};

	triggerDeleteMode = (groupId) => {
		this.selectedForDelete = groupId;
		this.isInDelete = true;
	};

	resetDeleteMode = () => {
		this.selectedForDelete = '';
		this.isInDelete = false;
	};

	deleteGroup = async () => {
		if (this.selectedForDelete === '') return;
		this.groupsMapped.set(this.selectedForDelete, {
			...this.groupsMapped.get(this.selectedForDelete),
			loading: true,
		});
		this.isDeleting = true;
		try {
			await client.mutate({
				mutation: DELETE_GROUP,
				variables: {
					groupId: this.selectedForDelete,
				},
				fetchPolicy: 'no-cache',
			});
			notificationStore.setNotification({
				...standardDeletedSuccess,
				header: 'Group deleted successfully!',
			});
			runInAction(() => {
				this.groupsMapped.delete(this.selectedForDelete);
				this.selectedForDelete = '';
				this.isInDelete = false;
				this.isDeleting = false;
				if ([...this.groupsMapped.values()].length === 0) {
					this.isNoGroups = true;
				}
			});
		} catch (e) {
			runInAction(() => {
				this.groupsMapped.set(this.selectedForDelete, {
					...this.groupsMapped.get(this.selectedForDelete),
					loading: false,
				});
				this.isDeleting = false;
			});

			console.error(e);
			notificationStore.setNotification(standardError);
		}
	};

	addGroup = (group) => {
		this.groupsMapped.set(group.id, {
			...group,
			loading: false,
		});
		this.isNoGroups = false;
	};

	getGroup = (groupId) => {
		return this.groupsMapped.get(groupId);
	};

	selectGroup = (groupId) => {
		this.selectedGroup = this.groupsMapped.get(groupId);
	};

	endSelection = () => {
		this.selectedGroup = null;
	};

	incrementMembersCount = (count, groupId) => {
		const group = this.groupsMapped.get(groupId);
		const countRes = group.membersCount + count;
		this.groupsMapped.set(groupId, {
			...this.groupsMapped.get(groupId),
			membersCount: countRes,
		});
	};

	get isGroupSelected() {
		return this.selectedGroup !== null;
	}
}

export const groupsStore = new GroupsStore();
